/**
 * Taxonomy admin — manage Work Order categories (2 levels) and Asset
 * types (3 levels) as a nested tree. The existing dropdowns elsewhere in
 * the app pull flat-list data from `/api/taxonomy/:kind`; the tree lives
 * purely in the UI via `parentId`/`depth` on each option.
 *
 * Removing a node soft-archives it AND every descendant so historical
 * records keep showing their label while the dropdowns stay clean.
 *
 * Implementation note: the tree is flattened to an ordered array with
 * depth attached to each row, then rendered as a plain list. We
 * previously used a self-recursive component, but babel-traverse ran into
 * a "Maximum call stack size exceeded" on some toolchains when JSX
 * elements call themselves — flattening sidesteps that entirely.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Check, X, Wrench, Layers, CornerDownRight,
  ChevronRight, ChevronDown,
} from 'lucide-react';

import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  listTaxonomy, createTaxonomyOption, updateTaxonomyOption, deleteTaxonomyOption,
} from '../../services/api';

// Max depth a given kind allows. Both kinds now support 5 levels
// total (depths 0..4); the backend enforces the same cap.
const MAX_DEPTH = { wo_category: 4, asset_type: 4 };

const KINDS = [
  {
    kind: 'wo_category', label: 'Work Order categories', icon: Wrench,
    description: 'Up to 5 levels. Example: Electrical → Reactive → Emergency call-out.',
  },
  {
    kind: 'asset_type', label: 'Asset types', icon: Layers,
    description: 'Up to 5 levels. Example: Industrial → Cable manufacturing → Extrusion line → Line A → Stage 2.',
  },
];

/**
 * Build an ordered flat list where each entry carries `{item, depth,
 * childCount, ancestorIds}`. Traversal is depth-first, children sorted
 * by sortOrder then label. Because `parentId` on older docs may be
 * missing, anything without a known parent is treated as top-level.
 * `ancestorIds` is the stack of every parent id up to the root — used
 * by the UI to hide rows whose any ancestor is collapsed.
 */
const flatten = (items) => {
  const byParent = new Map();
  const ids = new Set(items.map((i) => i.id));
  for (const it of items) {
    const pid = it.parentId && ids.has(it.parentId) ? it.parentId : null;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(it);
  }
  for (const arr of byParent.values()) {
    arr.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.label.localeCompare(b.label));
  }
  const out = [];
  const walk = (parentId, depth, ancestors) => {
    const kids = byParent.get(parentId) || [];
    for (const k of kids) {
      const childList = byParent.get(k.id) || [];
      out.push({ item: k, depth, childCount: childList.length, ancestorIds: ancestors });
      walk(k.id, depth + 1, [...ancestors, k.id]);
    }
  };
  walk(null, 0, []);
  return out;
};

/** Collect the id of every descendant of `rootId` — used to locally prune
 * archived subtrees from the UI after a cascading DELETE. Returns a Set
 * including `rootId` itself. */
const descendantIds = (rootId, items) => {
  const byParent = new Map();
  for (const it of items) {
    const pid = it.parentId || null;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(it.id);
  }
  const out = new Set([rootId]);
  const stack = [rootId];
  while (stack.length) {
    const n = stack.pop();
    for (const cid of (byParent.get(n) || [])) {
      out.add(cid); stack.push(cid);
    }
  }
  return out;
};

const TaxonomyList = ({ kind, label, icon: Icon, description }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // `addingUnder` is either `'ROOT'` for a new top-level row, a parent id
  // for a new child, or null when nothing is being added.
  const [addingUnder, setAddingUnder] = useState(null);
  const [newLabel, setNewLabel] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  // Ids of rows whose children are currently visible. Default: all
  // collapsed — keeps the page scannable once the tree has dozens of
  // leaves. Toggle via the chevron on each parent row.
  const [expanded, setExpanded] = useState(() => new Set());
  const maxDepth = MAX_DEPTH[kind] ?? 0;
  const toggleExpanded = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const refresh = async () => {
    setLoading(true);
    try { setItems(await listTaxonomy(kind)); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [kind]);

  const rows = useMemo(() => flatten(items), [items]);

  // IDs of every row that actually has children — these are the only
  // rows that carry an expansion state. We recompute on each tree change
  // so the "Expand all" / "Collapse all" buttons hide automatically when
  // the list becomes a flat one.
  const expandableIds = useMemo(
    () => rows.filter((r) => r.childCount > 0).map((r) => r.item.id),
    [rows],
  );
  const allExpanded = expandableIds.length > 0 && expandableIds.every((id) => expanded.has(id));
  const anyExpanded = expandableIds.some((id) => expanded.has(id));

  const submitAdd = async (parentId) => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    try {
      const created = await createTaxonomyOption(kind, trimmed, parentId);
      setItems((prev) => [...prev, created]);
      // Auto-expand the parent so the new row is immediately visible.
      if (parentId) {
        setExpanded((prev) => {
          const next = new Set(prev);
          next.add(parentId);
          return next;
        });
      }
      setNewLabel(''); setAddingUnder(null);
      toast.success(`"${created.label}" added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    }
  };

  const submitRename = async () => {
    const trimmed = editLabel.trim();
    if (!trimmed || !editingId) return;
    try {
      const updated = await updateTaxonomyOption(editingId, { label: trimmed });
      setItems((prev) => prev.map((x) => (x.id === editingId ? updated : x)));
      setEditingId(null); setEditLabel('');
      toast.success('Updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const removeItem = async (item, childCount) => {
    const suffix = childCount > 0 ? ` and ${childCount} child item${childCount === 1 ? '' : 's'}` : '';
    if (!window.confirm(`Remove "${item.label}"${suffix}?\n\nExisting records that already use these labels will keep showing them; they just won't appear in dropdowns for new records.`)) return;
    try {
      const res = await deleteTaxonomyOption(item.id);
      const dead = descendantIds(item.id, items);
      setItems((prev) => prev.filter((x) => !dead.has(x.id)));
      toast.success(res?.archivedCount > 1 ? `Removed ${res.archivedCount} items` : 'Removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`taxonomy-section-${kind}`}>
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="h-4 w-4 text-slate-500 shrink-0" />
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-900 truncate">{label}</h3>
            <p className="text-xs text-slate-500 truncate">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {expandableIds.length > 0 && (
            <Button
              size="sm" variant="ghost" className="h-8 px-2 text-xs text-slate-600"
              onClick={() => setExpanded(anyExpanded ? new Set() : new Set(expandableIds))}
              data-testid={`taxonomy-toggle-all-${kind}`}
              title={allExpanded ? 'Collapse all' : 'Expand all'}
            >
              {anyExpanded ? (
                <><ChevronDown className="h-3.5 w-3.5 mr-1" /> Collapse all</>
              ) : (
                <><ChevronRight className="h-3.5 w-3.5 mr-1" /> Expand all</>
              )}
            </Button>
          )}
          <Button
            size="sm" variant="outline"
            onClick={() => { setAddingUnder('ROOT'); setNewLabel(''); }}
            data-testid={`taxonomy-add-${kind}`}
          >
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
      </header>

      {addingUnder === 'ROOT' && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200">
          <Input
            autoFocus value={newLabel}
            placeholder={kind === 'asset_type' ? 'e.g. Industrial' : 'e.g. Electrical'}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitAdd(null);
              else if (e.key === 'Escape') { setAddingUnder(null); setNewLabel(''); }
            }}
            className="h-8 text-sm"
            data-testid={`taxonomy-add-input-${kind}`}
          />
          <Button size="sm" onClick={() => submitAdd(null)} data-testid={`taxonomy-add-save-${kind}`}>
            <Check className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setAddingUnder(null); setNewLabel(''); }}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      <div>
        {loading && <div className="px-4 py-6 text-center text-sm text-slate-500">Loading…</div>}
        {!loading && rows.length === 0 && addingUnder !== 'ROOT' && (
          <div className="px-4 py-6 text-center text-sm text-slate-500">
            No options yet. Add one to get started.
          </div>
        )}
        {!loading && rows.map(({ item, depth, childCount, ancestorIds }) => {
          // Hide any row whose any ancestor is currently collapsed.
          if (ancestorIds.some((aid) => !expanded.has(aid))) return null;
          const canNest = depth < maxDepth;
          const isEditing = editingId === item.id;
          const isExpanded = expanded.has(item.id);
          const indent = 16 + depth * 24;
          return (
            <React.Fragment key={item.id}>
              <div
                className="flex items-center gap-1 py-1.5 hover:bg-slate-50 border-b border-slate-50"
                style={{ paddingLeft: indent, paddingRight: 12 }}
                data-testid={`taxonomy-row-${item.id}`}
              >
                {childCount > 0 ? (
                  <button
                    type="button"
                    onClick={() => toggleExpanded(item.id)}
                    className="h-5 w-5 rounded hover:bg-slate-200 flex items-center justify-center shrink-0 text-slate-500"
                    title={isExpanded ? 'Collapse' : 'Expand'}
                    data-testid={`taxonomy-toggle-${item.id}`}
                  >
                    {isExpanded
                      ? <ChevronDown className="h-3.5 w-3.5" />
                      : <ChevronRight className="h-3.5 w-3.5" />}
                  </button>
                ) : (
                  // Keep the horizontal alignment of labels consistent
                  // between leaf and parent rows.
                  <span className="h-5 w-5 shrink-0" aria-hidden="true" />
                )}
                {depth > 0 && childCount === 0 && (
                  <CornerDownRight className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                )}
                {isEditing ? (
                  <>
                    <Input
                      autoFocus value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') submitRename();
                        else if (e.key === 'Escape') { setEditingId(null); setEditLabel(''); }
                      }}
                      className="h-7 text-sm flex-1 min-w-0"
                      data-testid={`taxonomy-edit-input-${item.id}`}
                    />
                    <Button size="sm" onClick={submitRename} className="h-7 px-2" data-testid={`taxonomy-edit-save-${item.id}`}>
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditLabel(''); }} className="h-7 px-2">
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm text-slate-900 truncate">{item.label}</span>
                    {canNest && (
                      <Button
                        size="icon" variant="ghost" className="h-6 w-6 text-indigo-600"
                        title="Add a sub-item"
                        onClick={() => {
                          // Expand the row so the inline input is visible.
                          setExpanded((prev) => {
                            const next = new Set(prev);
                            next.add(item.id);
                            return next;
                          });
                          setAddingUnder(item.id); setNewLabel('');
                        }}
                        data-testid={`taxonomy-add-child-${item.id}`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      size="icon" variant="ghost" className="h-6 w-6"
                      onClick={() => { setEditingId(item.id); setEditLabel(item.label); }}
                      title="Rename"
                      data-testid={`taxonomy-edit-${item.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5 text-slate-500" />
                    </Button>
                    <Button
                      size="icon" variant="ghost" className="h-6 w-6 text-red-600"
                      onClick={() => removeItem(item, childCount)}
                      title={childCount > 0 ? `Remove (plus ${childCount} child${childCount === 1 ? '' : 'ren'})` : 'Remove'}
                      data-testid={`taxonomy-delete-${item.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
              {addingUnder === item.id && (
                <div
                  className="flex items-center gap-2 py-2 bg-amber-50 border-b border-amber-200"
                  style={{ paddingLeft: indent + 24, paddingRight: 12 }}
                >
                  <Input
                    autoFocus value={newLabel}
                    placeholder={depth === 0 ? 'e.g. Reactive Electrical' : 'e.g. Extrusion line'}
                    onChange={(e) => setNewLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitAdd(item.id);
                      else if (e.key === 'Escape') { setAddingUnder(null); setNewLabel(''); }
                    }}
                    className="h-7 text-sm"
                    data-testid={`taxonomy-add-child-input-${item.id}`}
                  />
                  <Button size="sm" onClick={() => submitAdd(item.id)} className="h-7 px-2" data-testid={`taxonomy-add-child-save-${item.id}`}>
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setAddingUnder(null); setNewLabel(''); }} className="h-7 px-2">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
};

const Taxonomy = () => (
  <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-taxonomy">
    <PageHeader
      title="Lists"
      description="Manage the dropdown options used across the app. Sub-items nest up to the limit per list. Removing an item also archives its children — existing records keep their label."
    />
    {KINDS.map((k) => (
      <TaxonomyList key={k.kind} {...k} />
    ))}
  </div>
);

export default Taxonomy;
